import {
  Button,
  ButtonVariant,
  IconButton,
  IconIdentifier,
  toast,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { intersection, isEmpty, isNil, unionBy, uniqBy } from 'lodash';
import {
  WorkspaceRightSideCard,
  exitEditableLayer,
  refetchLayer,
  selectMap3DState,
  selectMap3dDataset,
  selectMap3dWorkspace,
  selectWorkspaceActionableLayers,
  setAIActiveOrthoLayerId,
  setAIMode,
  setRightSideCard,
  setShowUnsavedFeaturesModal,
  setWorkspaceInstruction,
  updateWorkspaceLayer,
} from 'map-3d/shared/map-3d-slices';
import {
  SelectToolEventType,
  TextboxEventType,
} from 'map-3d/shared/map-3d-tools';
import {
  useDrawingInProgress,
  useEditingLockState,
} from 'map-3d/shared/map-3d-tools/drawing-tools';
import { WorkspaceTools } from 'map-3d/shared/map-3d-tools/enums';
import React, { useContext, useEffect, useRef, useState } from 'react';
import type { Feature } from 'shared/api';
import {
  handleResponseErrorMessage,
  useBulkAddFeatures,
  useBulkDeleteFeatures,
  useBulkUpdateFeatures,
} from 'shared/api';
import type { GeometryType } from 'shared/cesium';
import { ConfirmationModal } from 'shared/components';
import { GlobalContext } from 'shared/context';
import { FeatureFlag } from 'shared/enums';
import { createFeatureFromWKT } from 'shared/helpers';
import { useKeyboardShortcuts } from 'shared/hooks';
import { usePolygonAISession } from 'shared/hooks/polygon-ai';
import { AddTextboxModal, UnsavedFeaturesModal } from '../modals';
import { isCursorWithinLayerBounds, zoomToLayer } from '../shared';
import { TOOLBOX_SHORTCUTS } from './constants';
import { ToolboxShortcutAction, UnsavedChangesActionType } from './enums';
import { getToolboxTitle, getWorkspaceInstruction } from './helpers';
import { PolygonSecondaryToolbox } from './polygon-secondary-toolbox';
import { type PendingUnsavedChangesAction } from './types';

export const WorkSpaceToolbox: React.FC = () => {
  // Context.
  const { loggedUser } = useContext(GlobalContext);

  // States.
  const [currentActiveTool, setCurrentActiveTool] =
    useState<WorkspaceTools | null>(null);
  const [showAddTextboxModal, setShowAddTextboxModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [selectedFeatureTypes, setSelectedFeatureTypes] = useState<
    GeometryType[]
  >([]);
  const [pendingAction, setPendingAction] =
    useState<PendingUnsavedChangesAction | null>(null);

  const isAnyFeatureSelected = !isEmpty(selectedFeatureTypes);

  // Selectors.
  const {
    drawingTools,
    currentEditableLayerId,
    showUnsavedFeaturesModal,
    actionTools,
    polygonAiConfig: polygonAi,
  } = useAppSelector(selectMap3dWorkspace);
  const { activeWorkspaceSite } = useAppSelector(selectMap3dDataset);
  const { cesiumProxy } = useAppSelector(selectMap3DState);

  const { currentEditableLayer } = useAppSelector(
    selectWorkspaceActionableLayers,
  );

  const isAiDigitizationEnabled =
    !!loggedUser?.featureFlags?.[FeatureFlag.AIAssistedDigitization];

  // Hooks.
  const dispatch = useAppDispatch();
  const polygonAISession = usePolygonAISession(isAiDigitizationEnabled);
  const editingLock = useEditingLockState(currentActiveTool);
  const isDrawingInProgress = useDrawingInProgress();
  const { getConfirmedPolygons, clearAfterSave: clearAIPolygonsAfterSave } =
    polygonAISession;

  // Constants.
  const styleOptions = {
    pointStyleOptions: currentEditableLayer?.featuresStyles?.point,
    polygonStyleOptions: currentEditableLayer?.featuresStyles?.polygon,
    lineStyleOptions: currentEditableLayer?.featuresStyles?.line,
    textboxStyleOptions: currentEditableLayer?.featuresStyles?.textbox,
  };
  const { isAiModeEnabled } = polygonAi;

  const isEditingPolygonOrLine = Boolean(
    drawingTools?.polygonTool.isEditingFeature ||
      drawingTools?.lineTool.isEditingFeature,
  );

  const shouldShowPolygonAIToolbox =
    currentActiveTool === WorkspaceTools.PolygonTool && isAiDigitizationEnabled;

  // Refs.
  const exportedFeatures = useRef<Feature[]>();
  const savedAIPolygonFeaturesRef = useRef<Feature[]>([]);

  /*
   * When the style changes, a new map layer is added with the updated style.
   * We need to track the latest mapLayer on unmount to change its visibility.
   * We cannot directly use the editableLayer.mapLayer state because we want the updated
   * mapLayer value on unmount. To get the updated state value, we would have to add
   * editableLayer.mapLayer to the dependency array of the useEffect performing cleanup.
   * By doing that, the cleanup function to clear the Cesium vector layer from the map
   * and add the imagery layer would run every time the editableLayer.mapLayer state changes,
   * whereas we want the cleanup function to run only on unmount.
   * Therefore, we use useRef to store the latest value of editableLayer?.mapLayer,
   * whose visibility needs to be changed when the toolbox is unmounted (i.e., when exiting editing mode).
   */
  const mapLayerRef = useRef(currentEditableLayer?.mapLayer);

  // Api.
  const {
    mutate: sendBulkAddFeatureRequest,
    data: bulkAddFeaturesResponse,
    isSuccess: isSuccessBulkAddFeatures,
    isPending: isLoadingBulkAddFeatures,
    isError: isErrorBulkAddFeatures,
    error: bulkAddFeaturesErrorResponse,
  } = useBulkAddFeatures();

  const {
    mutate: sendBulkUpdateFeatureRequest,
    data: bulkUpdateFeaturesResponse,
    isSuccess: isSuccessBulkUpdateFeatures,
    isPending: isLoadingBulkUpdateFeatures,
    isError: isErrorBulkUpdateFeatures,
    error: bulkUpdateFeaturesErrorResponse,
  } = useBulkUpdateFeatures();

  const {
    mutate: sendBulkDeleteFeatureRequest,
    data: bulkDeleteFeaturesResponse,
    isPending: isLoadingBulkDeleteFeature,
    isSuccess: isSuccessBulkDeleteFeatures,
    isError: isErrorBulkDeleteFeatures,
    error: bulkDeleteFeaturesErrorResponse,
  } = useBulkDeleteFeatures();

  // useEffect - Mount.
  useEffect(() => {
    // Activate default tool (i.e Select tool).
    onClickSelectTool();

    return () => {
      exitWorkspace();

      if (mapLayerRef.current) {
        mapLayerRef.current.show = true;
      }

      actionTools?.selectFeatureTool.removeEventListener(
        SelectToolEventType.FeatureSelected,
        selectFeatureListener,
      );
      actionTools?.selectFeatureTool.removeEventListener(
        SelectToolEventType.FeatureDeselected,
        selectFeatureListener,
      );
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setWorkspaceInstruction(null));
    };
  }, []);

  useEffect(() => {
    if (shouldShowPolygonAIToolbox) {
      return;
    }

    dispatch(
      setWorkspaceInstruction(
        getWorkspaceInstruction({
          activeTool: currentActiveTool,
          editingLock,
          selectedFeatureTypes,
          isDrawingInProgress,
          isEditingPolygonOrLine,
        }),
      ),
    );
  }, [
    shouldShowPolygonAIToolbox,
    editingLock,
    currentActiveTool,
    selectedFeatureTypes,
    isDrawingInProgress,
    isEditingPolygonOrLine,
  ]);

  // Storing the latest mapLayer
  useEffect(() => {
    if (!isNil(currentEditableLayer)) {
      mapLayerRef.current = currentEditableLayer.mapLayer;
    }
  }, [currentEditableLayer?.mapLayer]);

  // Select is torn down when the editable layer changes; turn it back on for the new layer.
  useEffect(() => {
    if (currentActiveTool !== WorkspaceTools.SelectFeatureTool) {
      return;
    }

    drawingTools?.activateEditingListeners();
    actionTools?.selectFeatureTool.activate({
      layerId: currentEditableLayerId,
    });
  }, [currentEditableLayerId]);

  // useEffect - Bulk Add response message handling.
  useEffect(() => {
    handleResponseErrorMessage(
      isErrorBulkAddFeatures,
      bulkAddFeaturesErrorResponse,
    );
  }, [isErrorBulkAddFeatures, bulkAddFeaturesErrorResponse]);

  // useEffect - Bulk Update response message handling.
  useEffect(() => {
    handleResponseErrorMessage(
      isErrorBulkUpdateFeatures,
      bulkUpdateFeaturesErrorResponse,
    );
  }, [isErrorBulkUpdateFeatures, bulkUpdateFeaturesErrorResponse]);

  // useEffect - Bulk Delete response message handling.
  useEffect(() => {
    handleResponseErrorMessage(
      isErrorBulkDeleteFeatures,
      bulkDeleteFeaturesErrorResponse,
    );
  }, [isErrorBulkDeleteFeatures, bulkDeleteFeaturesErrorResponse]);

  // useEffect - Bulk Add.
  useEffect(() => {
    if (isSuccessBulkAddFeatures && bulkAddFeaturesResponse) {
      // Reset all added drawn features array.
      drawingTools?.resetAllDrawnFeatureArrays(true);
      updateCurrentEditableLayer();
      clearAIPolygonsAfterSave();
      currentEditableLayer?.refresh?.();

      if (isAiModeEnabled) {
        dispatch(setShowUnsavedFeaturesModal(false));
        executePendingAction();
      }

      toast.success('Layer saved successfully');
    }
  }, [bulkAddFeaturesResponse, isSuccessBulkAddFeatures]);

  // useEffect - Bulk Update.
  useEffect(() => {
    if (isSuccessBulkUpdateFeatures && bulkUpdateFeaturesResponse) {
      // Reset all edited drawn features array.
      drawingTools?.resetAllEditedFeatureArrays();

      if (currentEditableLayer?.id) {
        updateCurrentEditableLayer();
        // Refetch layer data to update feature count.
        dispatch(refetchLayer({ id: currentEditableLayer.id }));
      }

      toast.success('Layer saved successfully');
    }
  }, [bulkUpdateFeaturesResponse, isSuccessBulkUpdateFeatures]);

  useEffect(() => {
    if (isSuccessBulkDeleteFeatures && bulkDeleteFeaturesResponse) {
      toast.success('Feature(s) deleted successfully');
      actionTools?.selectFeatureTool.deleteSelectedFeatures();
      drawingTools?.resetAllDeletedFeatureArrays();
      setShowDeleteConfirmationModal(false);
      updateCurrentEditableLayer();

      setSelectedFeatureTypes([]);
    }
  }, [isSuccessBulkDeleteFeatures, bulkDeleteFeaturesResponse]);

  // useEffect - Handle warning message for unsaved features when clicked on back, reload and close button.
  useEffect(() => {
    /**
     * Back button resets the state even after doing preventDefault.
     * Adding a state using push state prevents this behaviour.
     */
    window.history.pushState({ currentActiveTool }, '', '');
  }, []);

  useEffect(() => {
    const hasUnsavedChanges = () =>
      drawingTools?.checkUnsavedFeatures() || !isEmpty(getConfirmedPolygons());

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges()) {
        event.preventDefault();
        dispatch(setShowUnsavedFeaturesModal(true));
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        event.preventDefault();
        // A non-empty string is required for Chrome browsers to display confirmation message.
        event.returnValue = '';
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [drawingTools, getConfirmedPolygons]);

  // Helpers.
  const executePendingAction = () => {
    if (!pendingAction) {
      return;
    }

    switch (pendingAction.type) {
      case UnsavedChangesActionType.DisableAIMode: {
        dispatch(setAIMode(false));
        drawingTools?.polygonTool?.activate();

        break;
      }

      case UnsavedChangesActionType.ChangeOrthomosaic: {
        dispatch(setAIActiveOrthoLayerId(pendingAction.layer.id));

        if (
          !isCursorWithinLayerBounds(
            pendingAction.layer,
            cesiumProxy?.lastMouseLocation,
          )
        ) {
          zoomToLayer(pendingAction.layer, cesiumProxy, activeWorkspaceSite);
        }

        break;
      }

      case UnsavedChangesActionType.ExitWorkspace:
        exitWorkspace(true, true);
        break;

      case UnsavedChangesActionType.SwitchTool: {
        switch (pendingAction.tool) {
          case WorkspaceTools.SelectFeatureTool:
            activateSelectTool();
            break;
          case WorkspaceTools.PointTool:
            activatePointTool();
            break;
          case WorkspaceTools.LineTool:
            activateLineTool();
            break;
          case WorkspaceTools.PolygonTool:
            activatePolygonTool();
            break;
          case WorkspaceTools.TextboxTool:
            activateTextboxTool();
            break;
        }
        break;
      }
    }

    setPendingAction(null);
  };

  // Handlers
  const onClickExit = () => {
    if (
      drawingTools?.checkUnsavedFeatures() ||
      !isEmpty(getConfirmedPolygons())
    ) {
      setPendingAction({
        type: UnsavedChangesActionType.ExitWorkspace,
      });

      dispatch(setShowUnsavedFeaturesModal(true));
    } else {
      /**
       * Currently, we are removing tile from cache in aereo cesium,
       * so we don't need to refresh layer on exit.
       */
      exitWorkspace();
    }
  };

  // Separate Handler for Save and Exit button in modal b/c currentEditableLayer should also be set to null which is not the case when we do save from the toolbox.
  const onSaveAndExitModal = () => {
    onSaveFeatures();

    if (!isAiModeEnabled) {
      exitWorkspace(true, true);
      dispatch(setShowUnsavedFeaturesModal(false));
    }
  };

  const onExitUnsavedChangesModal = () => {
    dispatch(setShowUnsavedFeaturesModal(false));

    if (!isAiModeEnabled) {
      if (currentEditableLayerId && drawingTools) {
        exitWorkspace();
      }

      return;
    }

    clearAIPolygonsAfterSave();
    executePendingAction();
  };

  const handleToolSwitch = (
    tool: WorkspaceTools,
    activateTool: VoidFunction,
  ) => {
    if (isAiModeEnabled && !isEmpty(getConfirmedPolygons())) {
      setPendingAction({
        type: UnsavedChangesActionType.SwitchTool,
        tool,
      });

      dispatch(setShowUnsavedFeaturesModal(true));
      return;
    }

    activateTool();
  };

  const onClickPointTool = () =>
    handleToolSwitch(WorkspaceTools.PointTool, activatePointTool);

  const onClickLineTool = () =>
    handleToolSwitch(WorkspaceTools.LineTool, activateLineTool);

  const onClickTextboxTool = () =>
    handleToolSwitch(WorkspaceTools.TextboxTool, activateTextboxTool);

  const onClickSelectTool = () =>
    handleToolSwitch(WorkspaceTools.SelectFeatureTool, activateSelectTool);

  const onClickPolygonTool = () => {
    activatePolygonTool();
  };

  const onClickDelete = () => {
    if (drawingTools) {
      const selectedIds = actionTools?.selectFeatureTool.selectedFeatures.map(
        (feature) => feature.id,
      );

      if (selectedIds) {
        if (
          intersection(selectedIds, drawingTools.loadedFeatureIds).length > 0
        ) {
          sendBulkDeleteFeatureRequest(selectedIds);
        } else {
          actionTools?.selectFeatureTool.deleteSelectedFeatures();
          drawingTools?.resetAllDeletedFeatureArrays();
          setShowDeleteConfirmationModal(false);
        }
      }
    }
  };

  const onSaveFeatures = () => {
    if (currentEditableLayerId && drawingTools) {
      const newFeatures: Feature[] = drawingTools.exportNewFeatures(
        currentEditableLayerId,
      );

      const editedFeatures: Feature[] = drawingTools.exportEditedFeatures(
        currentEditableLayerId,
      );

      const polygonAIFeatures = getConfirmedPolygons().map((wkt) =>
        createFeatureFromWKT(wkt, currentEditableLayerId),
      );

      savedAIPolygonFeaturesRef.current = polygonAIFeatures;

      const allNewFeatures = [...newFeatures, ...polygonAIFeatures];

      exportedFeatures.current = unionBy(allNewFeatures, editedFeatures, 'id');

      // Remove FeatureObj (feature key) from exported features.
      const modifiedNewFeatures = allNewFeatures.map((feature) => {
        return { ...feature, feature: undefined };
      });

      // Only send updates for persisted features. If a feature was newly created
      // and edited before first save, it should be treated as a create only.
      const persistedFeatureIds = new Set(drawingTools.loadedFeatureIds);
      const filteredEditedFeatures = editedFeatures.filter((feature) =>
        persistedFeatureIds.has(feature.id),
      );

      const modifiedEditedFeatures = filteredEditedFeatures.map((feature) => {
        return { ...feature, feature: undefined, layer: undefined };
      });

      if (!isEmpty(modifiedNewFeatures)) {
        sendBulkAddFeatureRequest(modifiedNewFeatures);
      }

      if (!isEmpty(modifiedEditedFeatures)) {
        sendBulkUpdateFeatureRequest(modifiedEditedFeatures);
      }
    }
  };

  const handleAddTextbox = () => {
    displayAddTextboxModal();
  };

  const onAddTextBoxClick = (text: string) => {
    drawingTools?.textboxTool.createTextbox(text);
    hideAddTextboxModal();
  };

  // Modal handlers.
  const displayAddTextboxModal = () => {
    setShowAddTextboxModal(true);
  };

  const hideAddTextboxModal = () => {
    setShowAddTextboxModal(false);
  };

  // Helpers.
  const deactivateActionTools = () => {
    actionTools?.deactivateAllTools();
    setSelectedFeatureTypes([]);
  };

  const activateSelectTool = () => {
    const selectFeatureTool = actionTools?.selectFeatureTool;

    if (currentActiveTool !== WorkspaceTools.SelectFeatureTool) {
      setCurrentActiveTool(WorkspaceTools.SelectFeatureTool);
      drawingTools?.activateEditingListeners();

      selectFeatureTool?.activate({
        layerId: currentEditableLayerId,
      });
      selectFeatureTool?.addEventListener(
        SelectToolEventType.FeatureSelected,
        selectFeatureListener,
      );
      selectFeatureTool?.addEventListener(
        SelectToolEventType.FeatureDeselected,
        selectFeatureListener,
      );
    } else {
      setCurrentActiveTool(null);
      setSelectedFeatureTypes([]);
      selectFeatureTool?.deselectSelectedFeatures();
      selectFeatureTool?.deactivate();
      selectFeatureTool?.removeEventListener(
        SelectToolEventType.FeatureSelected,
        selectFeatureListener,
      );
    }
  };

  const activatePointTool = () => {
    if (currentActiveTool === WorkspaceTools.PointTool) {
      return;
    }

    setCurrentActiveTool(WorkspaceTools.PointTool);
    deactivateActionTools();
    drawingTools?.activatePointTool(
      { layerId: currentEditableLayerId },
      styleOptions,
    );
  };

  const activateLineTool = () => {
    if (currentActiveTool === WorkspaceTools.LineTool) {
      return;
    }

    setCurrentActiveTool(WorkspaceTools.LineTool);
    deactivateActionTools();
    drawingTools?.activateLineTool(
      { layerId: currentEditableLayerId, isLabelSizeFixed: false },
      styleOptions,
    );
  };

  const activatePolygonTool = () => {
    if (currentActiveTool === WorkspaceTools.PolygonTool) {
      return;
    }

    setCurrentActiveTool(WorkspaceTools.PolygonTool);
    deactivateActionTools();
    drawingTools?.activatePolygonTool(
      { layerId: currentEditableLayerId, isLabelSizeFixed: false },
      styleOptions,
    );
  };

  const activateTextboxTool = () => {
    if (currentActiveTool === WorkspaceTools.TextboxTool) {
      return;
    }

    setCurrentActiveTool(WorkspaceTools.TextboxTool);
    deactivateActionTools();
    drawingTools?.activateTextboxTool(
      { layerId: currentEditableLayerId },
      styleOptions,
    );
    drawingTools?.textboxTool.addEventListener(
      TextboxEventType.ReadyTextboxDraw,
      handleAddTextbox,
    );
  };

  const exitWorkspace = (saveFeatures = false, refreshLayer = true): void => {
    if (saveFeatures) {
      updateCurrentEditableLayer();
    }
    drawingTools?.resetAllUnsavedFeatures(saveFeatures);

    dispatch(
      exitEditableLayer({
        refreshRequired: refreshLayer,
      }),
    );

    dispatch(setRightSideCard(WorkspaceRightSideCard.None));
  };

  const updateCurrentEditableLayer = () => {
    if (isNil(currentEditableLayer)) {
      return;
    }

    const updatedFeatures: Feature[] = [];

    if (exportedFeatures.current) {
      updatedFeatures.push(...exportedFeatures.current);
    }

    if (currentEditableLayer.features) {
      updatedFeatures.push(...currentEditableLayer.features);
    }

    const uniqueUpdatedFeatures = uniqBy(updatedFeatures, (val) => val.id);

    // Store features into layer object.
    const updatedVectorLayer = {
      [currentEditableLayer.id]: {
        ...currentEditableLayer,
        features: uniqueUpdatedFeatures,
      },
    };

    if (bulkAddFeaturesResponse) {
      updatedVectorLayer[currentEditableLayer.id].featuresCount = {
        ...bulkAddFeaturesResponse.data.featuresCount,
      };
    }

    if (bulkDeleteFeaturesResponse) {
      updatedVectorLayer[currentEditableLayer.id].featuresCount = {
        ...bulkDeleteFeaturesResponse.data.featuresCount,
      };
    }

    dispatch(updateWorkspaceLayer(updatedVectorLayer));
  };

  const selectFeatureListener = () => {
    if (actionTools?.selectFeatureTool) {
      setSelectedFeatureTypes(
        actionTools.selectFeatureTool.selectedFeatures.map((feature) => {
          return feature.properties.type as GeometryType;
        }),
      );
    }
  };

  // Shortcuts.
  const isSavingFeatures =
    isLoadingBulkAddFeatures || isLoadingBulkUpdateFeatures;
  const isAnyModalOpen =
    showAddTextboxModal ||
    showDeleteConfirmationModal ||
    showUnsavedFeaturesModal;

  useKeyboardShortcuts(
    [
      {
        ...TOOLBOX_SHORTCUTS[ToolboxShortcutAction.SelectTool],
        handler: onClickSelectTool,
      },
      {
        ...TOOLBOX_SHORTCUTS[ToolboxShortcutAction.PointTool],
        handler: onClickPointTool,
      },
      {
        ...TOOLBOX_SHORTCUTS[ToolboxShortcutAction.LineTool],
        handler: onClickLineTool,
      },
      {
        ...TOOLBOX_SHORTCUTS[ToolboxShortcutAction.PolygonTool],
        handler: onClickPolygonTool,
      },
      {
        ...TOOLBOX_SHORTCUTS[ToolboxShortcutAction.TextboxTool],
        handler: onClickTextboxTool,
      },
      {
        ...TOOLBOX_SHORTCUTS[ToolboxShortcutAction.Delete],
        handler: () => setShowDeleteConfirmationModal(true),
        enabled: isAnyFeatureSelected,
      },
      {
        ...TOOLBOX_SHORTCUTS[ToolboxShortcutAction.Save],
        handler: onSaveFeatures,
        enabled: !isSavingFeatures,
      },
      {
        ...TOOLBOX_SHORTCUTS[ToolboxShortcutAction.Exit],
        handler: onClickExit,
        enabled: !isSavingFeatures && !isLoadingBulkDeleteFeature,
      },
    ],
    { enabled: !isAnyModalOpen },
  );

  return (
    <>
      <div className="workspace-toolbox">
        {shouldShowPolygonAIToolbox && (
          <PolygonSecondaryToolbox
            polygonAISession={polygonAISession}
            setPendingAction={setPendingAction}
          />
        )}

        <div className="workspace-toolbox__primary-toolbox">
          <div className="workspace-toolbox__feature-actions">
            <IconButton
              title={getToolboxTitle(
                TOOLBOX_SHORTCUTS[ToolboxShortcutAction.Delete],
              )}
              className="delete-btn"
              iconIdentifier={IconIdentifier.Bin}
              onClick={() => setShowDeleteConfirmationModal(true)}
              disabled={!isAnyFeatureSelected}
            />
            <IconButton
              title={getToolboxTitle(
                TOOLBOX_SHORTCUTS[ToolboxShortcutAction.SelectTool],
              )}
              className="selection-btn"
              iconIdentifier={IconIdentifier.Cursor}
              onClick={onClickSelectTool}
              isActive={currentActiveTool === WorkspaceTools.SelectFeatureTool}
            />
          </div>
          <div className="workspace-toolbox__drawing-tools">
            <IconButton
              title={getToolboxTitle(
                TOOLBOX_SHORTCUTS[ToolboxShortcutAction.PointTool],
              )}
              iconIdentifier={IconIdentifier.Point}
              className="point-tool-btn"
              onClick={onClickPointTool}
              isActive={
                currentActiveTool === WorkspaceTools.PointTool &&
                drawingTools?.pointTool.isActive
              }
            />

            <IconButton
              title={getToolboxTitle(
                TOOLBOX_SHORTCUTS[ToolboxShortcutAction.LineTool],
              )}
              iconIdentifier={IconIdentifier.Line}
              className="line-tool-btn"
              onClick={onClickLineTool}
              isActive={
                currentActiveTool === WorkspaceTools.LineTool &&
                drawingTools?.lineTool.isActive
              }
            />

            <IconButton
              title={getToolboxTitle(
                TOOLBOX_SHORTCUTS[ToolboxShortcutAction.PolygonTool],
              )}
              iconIdentifier={IconIdentifier.Polygon}
              className="polygon-tool-btn"
              onClick={onClickPolygonTool}
              isActive={
                isAiModeEnabled ||
                (currentActiveTool === WorkspaceTools.PolygonTool &&
                  drawingTools?.polygonTool.isActive)
              }
            />
            <IconButton
              title={getToolboxTitle(
                TOOLBOX_SHORTCUTS[ToolboxShortcutAction.TextboxTool],
              )}
              iconIdentifier={IconIdentifier.Textbox}
              className="textbox-tool-btn"
              onClick={onClickTextboxTool}
              isActive={
                currentActiveTool === WorkspaceTools.TextboxTool &&
                drawingTools?.textboxTool.isActive
              }
            />
          </div>
          <div className="workspace-toolbox__layer-actions">
            <Button
              title={getToolboxTitle(
                TOOLBOX_SHORTCUTS[ToolboxShortcutAction.Save],
              )}
              rightIconIdentifier={IconIdentifier.Save}
              variant={ButtonVariant.Link}
              onClick={onSaveFeatures}
              isLoading={isSavingFeatures}
            >
              Save
            </Button>
            <Button
              title={getToolboxTitle(
                TOOLBOX_SHORTCUTS[ToolboxShortcutAction.Exit],
              )}
              rightIconIdentifier={IconIdentifier.BoxArrowOutToRight}
              variant={ButtonVariant.Link}
              onClick={onClickExit}
              disabled={isSavingFeatures || isLoadingBulkDeleteFeature}
            >
              Exit
            </Button>
          </div>
          <UnsavedFeaturesModal
            show={showUnsavedFeaturesModal}
            onSaveAndExit={onSaveAndExitModal}
            onExit={onExitUnsavedChangesModal}
            bodyMessage={
              isAiModeEnabled
                ? 'You are exiting AI Mode. Do you want to save the AI-detected polygons before continuing?'
                : 'You are exiting digitisation mode. Do you want to save your changes before leaving?'
            }
            formMessage={
              isAiModeEnabled
                ? 'AI-detected polygons can only be edited using select tool once saved.'
                : undefined
            }
            showFormMessage={isAiModeEnabled}
            exitButtonLabel={isAiModeEnabled ? 'Discard Changes' : undefined}
            saveButtonLabel={isAiModeEnabled ? 'Save Changes' : undefined}
          />

          {showAddTextboxModal && (
            <AddTextboxModal
              onHide={hideAddTextboxModal}
              onAddTextBoxClick={onAddTextBoxClick}
            />
          )}

          {showDeleteConfirmationModal && (
            <ConfirmationModal
              message="Are you sure you want to delete?"
              onSubmit={onClickDelete}
              onClose={() => setShowDeleteConfirmationModal(false)}
            />
          )}
        </div>
      </div>
    </>
  );
};
